import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getApiUrl } from '@/config/api';
import { JMS_KEYS } from '@/config/jmsStorage';
import { store } from '@/app/store';
import { startRefreshing, refreshFailed, refreshSuccess, updateTokens, logout } from '@/app/store/slices/authSlice';
import { isTokenExpired } from '@/utils/token';
import { getDeviceSignature } from '@/utils/deviceSignature';
import { captureApiError, addBreadcrumb } from '@/observability/errorReporter';

let isRefreshing = false;
const failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue.length = 0;
};

const axiosInstance = axios.create({
  baseURL: getApiUrl(''),
  headers: {
    'Content-Type': 'application/json',
  },
});

const rawAxios = axios.create({
  baseURL: getApiUrl(''),
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    addBreadcrumb('http', `${(config.method ?? 'get').toUpperCase()} ${config.url ?? ''}`);
    const state = store.getState();
    const accessToken = state.auth.tokens.accessToken;

    if (accessToken) {
      if (isTokenExpired(accessToken)) {
        const isOnline = navigator.onLine;
        if (isOnline) {
          store.dispatch(logout());
          localStorage.removeItem(JMS_KEYS.user);
          localStorage.removeItem(JMS_KEYS.accessToken);
          localStorage.removeItem(JMS_KEYS.refreshToken);
          return Promise.reject(new Error('Token expired'));
        }
      }
      config.headers.Authorization = `Bearer ${accessToken}`;
      config.headers['X-Device-Signature'] = getDeviceSignature();
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    captureApiError(error);
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const state = store.getState();
      const refreshToken = state.auth.tokens.refreshToken;

      if (!refreshToken) {
        isRefreshing = false;
        store.dispatch(logout());
        localStorage.removeItem(JMS_KEYS.user);
        localStorage.removeItem(JMS_KEYS.accessToken);
        localStorage.removeItem(JMS_KEYS.refreshToken);
        processQueue(new Error('No refresh token'), null);
        return Promise.reject(error);
      }

      store.dispatch(startRefreshing());

      try {
        const response = await rawAxios.post<{
          success: boolean;
          statusCode: number;
          message: string;
          data: {
            user: {
              id: string;
              email: string;
              firstName: string;
              lastName: string;
            };
            tokens: {
              accessToken: string;
              refreshToken: string;
            };
          };
        }>('auth/refresh', { refreshToken, deviceSignature: getDeviceSignature() });
        const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;

        store.dispatch(updateTokens({ accessToken, refreshToken: newRefreshToken }));
        store.dispatch(refreshSuccess());
        isRefreshing = false;

        localStorage.setItem(JMS_KEYS.accessToken, accessToken);
        localStorage.setItem(JMS_KEYS.refreshToken, newRefreshToken);

        processQueue(null, accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        return axiosInstance(originalRequest);
      } catch (refreshError: any) {
        isRefreshing = false;
        const isNetworkError =
          !refreshError.response ||
          refreshError.code === 'ERR_NETWORK' ||
          refreshError.message?.includes('network');

        if (isNetworkError) {
          store.dispatch(refreshFailed('Network error during token refresh'));
          processQueue(refreshError, null);
          return Promise.reject(refreshError);
        }

        store.dispatch(refreshFailed('Token refresh failed'));
        store.dispatch(logout());
        localStorage.removeItem(JMS_KEYS.user);
        localStorage.removeItem(JMS_KEYS.accessToken);
        localStorage.removeItem(JMS_KEYS.refreshToken);
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export { axiosInstance, rawAxios };
